import {
  Box,
  CircularProgress,
  ClickAwayListener,
  Grid,
  PaginationItem,
  Popover,
  Typography,
  useMediaQuery,
} from '@mui/material';
import MuiPagination from '@mui/material/Pagination';
import { styled } from '@mui/material/styles';
import {
  DataGridPro,
  gridPageCountSelector,
  GridPagination,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import React, {
  memo,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import {
  deleteTimesheets,
  queryForApproveTimesheet,
  updateTimesheets,
} from '../../../../packages/common/api';
import WarningModal from '../../../../packages/common/shared-ui/src/components/WarningModal';
import CheckboxIcon from '../../../../packages/common/shared-ui/src/icons/CheckboxIcon';
import CheckboxIconChecked from '../../../../packages/common/shared-ui/src/icons/CheckboxIconChecked';
import ChevronIcon from '../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import PaginationNextButtonIcon from '../../../../packages/common/shared-ui/src/icons/PaginationNextButtonIcon';
import PaginationPreviousButtonIcon from '../../../../packages/common/shared-ui/src/icons/PaginationPreviousButtonIcon';
import ParentIssueDropdown from '../../../../packages/common/shared-ui/src/icons/ParentIssueDropdown';
import SortIcon from '../../../../packages/common/shared-ui/src/icons/SortIcon';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';
import { generateRedirectUrl } from '../../../../packages/common/utils/getRedirectUrl';
import { store } from '../../app/redux';
import TableSecondStepPopover from '../TableSecondStepPopover';

// Original class: contextPopover
const StyledContextPopover = styled(Popover)(({ theme }) => ({
  transform: 'unset !important',
  height: 'fit-content',
  borderRadius: theme.spacing(4),

  '& .MuiPaper-root': {
    overflowY: 'hidden',
    maxWidth: 'unset',
    maxHeight: 'unset',
    width: '100%',
    borderRadius: theme.spacing(4),
    // boxShadow: '0px 5px 10px -8px rgba(33, 35, 70, 1)',
  },

  '&::-webkit-scrollbar-track': {
    backgroundColor: '#F1F2F8',
    marginRight: '2px',
  },

  '&::-webkit-scrollbar': {
    width: '4px',
    backgroundColor: 'white !important',
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#7174AC',
    borderRadius: '5px',
  },

  '& .MuiBackdrop-root': {
    display: 'none',
    position: 'unset',
  },
}));

// Original class: contextMenuItem
const StyledContextMenuItem = styled(Grid, {
  shouldForwardProp: prop => !['disabledStyle'].includes(prop),
})(({ theme, disabledStyle }) => ({
  cursor: 'pointer',

  // transition: 'all .2s',
  height: '32px',

  '&:hover': {
    backgroundColor: 'rgba(220, 223, 244, 1)',
    '& svg path': {
      fill: '#212346',
    },
  },
  ...(disabledStyle && {
    cursor: 'not-allowed',
    pointerEvents: 'none',
    opacity: 0.5,
  }),
}));
// Original class: DataGridTableRoot
const StyledDataGridTableRoot = styled(DataGridPro)(({ theme }) => ({
  width: '100%',
  overflow: 'hidden',

  '&.MuiDataGrid-root': {
    border: 0,
    '& .MuiDataGrid-columnHeaders': {
      borderBottom: '1px solid #E4E4EF',
      '& .MuiBadge-badge': {
        display: 'none',
      },
      '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
        '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
          '& + .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
            borderLeft: '1px solid #E4E4EF',
            '&.actions': {
              borderLeft: 'none',
            },
          },
        },
      },
      '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor.MuiDataGrid-columnHeaderCheckbox':
        {
          minWidth: '36px!important',
          maxWidth: '36px!important',
          '& + .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
            paddingLeft: 0,
          },
        },
    },
    '& .MuiDataGrid-virtualScroller': {
      '&::-webkit-scrollbar': {
        height: '6px',
        width: '6px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#7174AC',
        borderRadius: '5px',
      },
    },
    '& .MuiDataGrid-row': {
      width: '100%',
      borderBottom: '1px solid #E4E4EF',
      minHeight: 'auto!important',
      maxHeight: 'none!important',
      '--height': 'auto!important',
      '& .MuiDataGrid-rowReorderCellContainer': {
        borderRight: '1px solid #E4E4EF',
      },
      '&.errorRow': {
        outline: '1px solid #E03737',
      },
      '& .MuiDataGrid-cell': {
        '&.tableCell': {
          overflow: 'visible',
          '&.parent': {
            paddingLeft: 0,
          },
          '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
            '& + .MuiDataGrid-cell.tableCell': {
              borderLeft: '1px solid #E4E4EF',
            },
          },
          '&.tableGroupCell': {
            overflow: 'visible',
            whiteSpace: 'nowrap',
            '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
              '& + .MuiDataGrid-cell.tableCell': {
                borderLeft: '1px solid transparent',
              },
            },
          },
          '&.tableGroupGanttCell': {
            padding: '10px 10px 10px 0',
            borderLeft: '1px solid #E4E4EF',
          },
        },
      },
      '& .MuiDataGrid-cell.MuiDataGrid-cellCheckbox': {
        minWidth: '36px!important',
        maxWidth: '36px!important',
        '& + .MuiDataGrid-cell': {
          paddingLeft: 0,
        },
      },
      '&.MuiDataGrid-row--editing': {
        boxShadow: 'none',
      },
    },
    '& .MuiDataGrid-row.groupRow': {
      '&.confirmationRow': {
        borderTop: '4px solid #E4E4EF',
        '&.first-row': {
          borderTop: 'none',
        },
      },
      '& .MuiDataGrid-cellCheckbox': {
        boxShadow: 'none',
        '& .MuiCheckbox-root': {
          visibility: 'hidden',
        },
      },
    },
    '& .MuiDataGrid-row .MuiDataGrid-cell:last-child': {
      borderRight: 0,
    },
    '& .MuiDataGrid-row.Mui-selected': {
      backgroundColor: '#DCDFF4',
    },
    '& .MuiDataGrid-row.Mui-selected.Mui-hovered': {
      backgroundColor: '#DCDFF4',
    },
    '& .MuiDataGrid-main .MuiDataGrid-iconButtonContainer .MuiButtonBase-root':
      {
        marginLeft: theme.spacing(2),
      },
    '& .MuiCheckbox-root': {
      '& .MuiSvgIcon-root': {
        width: '12px',
      },
    },
    '& .MuiDataGrid-columnSeparator': {
      '& .MuiSvgIcon-root': {
        width: '24px',
      },
    },
    '& .MuiDataGrid-cell': {
      borderBottom: 'none',
      '&.MuiDataGrid-cell--editing': {
        boxShadow: 'none',
        '&:focus-within': {
          outline: 'none',
        },
      },
      '&.tableCell': {
        wordBreak: 'break-word',
        borderColor: 'transparent',
        position: 'relative',
        padding: '10px',
        //overflow: 'hidden!important',
        whiteSpace: 'normal',
        '&.tableSelectCell': {
          overflow: 'visible',
        },
        '& a': {
          position: 'relative',
        },
        '&:last-child': {
          boxShadow: 0,
        },
        '& .MuiCheckbox-root': {
          padding: 0,
        },
        '&.cellEdit': {
          padding: 0,
        },
        '&.selectCell': {
          padding: 0,
        },
      },
      '&.actions': {
        padding: 0,
        '& .MuiDataGrid-actionsCell': {
          height: '100%',
          width: '100%',
          gridGap: 0,
        },
        '& .MuiButtonBase-root.MuiIconButton-root': {
          height: '100%',
          borderRadius: '0',
          padding: '0',
          flex: 1,
          '& .MuiSvgIcon-root': {
            width: '16px',
            height: '16px',
          },
          '& + .MuiButtonBase-root.MuiIconButton-root': {
            borderLeft: '1px solid #E4E4EF',
          },
        },
      },
      '& a': {
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
    '& .MuiDataGrid-cellCheckbox': {
      borderColor: 'transparent',
    },
    '& .MuiDataGrid-main': {
      borderRadius: '16px',
      backgroundColor: '#fff',
    },
  },

  '& .MuiDataGrid-footerContainer': {
    marginTop: '20px',
    minHeight: 'auto',
    border: 'none',
    justifyContent: 'flex-start',
    '& .MuiToolbar-root.MuiTablePagination-toolbar': {
      minHeight: 'auto',
      padding: 0,
      '& .MuiPagination-root': {
        marginLeft: 0,
        '& li': {
          fontSize: '16px',
          lineHeight: '20px',
        },
        '& li + li': {
          marginLeft: '2px',
        },
        '& .MuiButtonBase-root.MuiPaginationItem-root': {
          color: '#41424E',
          fontSize: '16px',
          lineHeight: '20px',
          borderRadius: '4px',
          minWidth: '28px',
          height: '28px',
          margin: 0,
          '&.Mui-selected': {
            backgroundColor: '#6270D0',
            color: '#FAFAFC',
            '&:hover': {
              color: '#FAFAFC',
              backgroundColor: '#6270D0',
            },
          },
          '& .MuiSvgIcon-root.MuiPaginationItem-icon': {
            width: 'auto',
            height: 'auto',
          },
          '&:hover': {
            color: '#6270D0',
            backgroundColor: 'transparent',
          },
        },
        '& .MuiPaginationItem-ellipsis': {
          fontSize: '16px',
          lineHeight: '20px',
          color: '#41424E',
          padding: 0,
          margin: 0,
          minWidth: ' 28px',
        },
      },
    },
  },
}));

const SpentTimeTable = ({
  approveOrRejectTimesheets,
  approveOrRejectOvertime,
  contextMenuData,
  setContextMenuData,
  currentType,
  columnsData,
  rowsData,
  changePagination,
  changeSorting,
  paginationData,
  editEvent,
  deleteTimesheet,
}) => {
  const getLocalString = useTranslation();
  const pageUseParams = useParams();
  const [tableSelectedValues, setTableSelectedValues] = useState([]);
  const isSmallScreen = useMediaQuery(theme =>
    theme.breakpoints.maxWidth('lg'),
  );
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const [sortModel, setSortModel] = useState([]);
  const [, forceUpdate] = useReducer(x => x - 1, 0);
  const [gridRows, setGridRows] = useState([]);
  const [gridColumns, setGridColumns] = useState([]);
  const [pagination, setPagination] = useState({ count: 0, page: 0 });
  const [tableErrorMessage, setTableErrorMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openWarningModal, setOpenWarningModal] = useState(false);
  const handleGetRedirectUrl = useCallback(
    ({ id, columnType }) => {
      const issueLink = pageUseParams.projectId
        ? `/projects/${pageUseParams.projectId}/issues/${id}`
        : `/issues/${id}`;
      switch (columnType) {
        case 'user':
          return generateRedirectUrl({
            host: window.location.origin,
            path: `/users/${id}`,
          });
        case 'project':
          return `${window.location.origin}/projects/${id}`;
        case 'issue':
          return issueLink;
        default:
          return '';
      }
    },
    [pageUseParams.projectId],
  );

  const cellRenderer = useCallback(
    ({ props, column }) => {
      const linkFields = ['project', 'issue'];
      if (!props.value && props.row.groupName) {
        if (props.colDef.position === 0) {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Typography
                variant='h4'
                sx={{
                  marginRight: '10px',
                  fontWeight: 700,
                }}
              >
                {props.row.groupName}
              </Typography>
              {props.row.groupData}
            </Box>
          );
        }
      } else {
        if (linkFields.includes(column.fieldType)) {
          const splittedColumnName = props?.value?.split('$$');
          let id = '';
          column.fieldType === 'project' && (id = props.row.services.projectId);
          column.fieldType === 'issue' && (id = props.row.services.issueId);
          return (
            <Box
              sx={{
                wordBreak: 'break-word',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Link
                to={handleGetRedirectUrl({
                  id: id,
                  columnType: column.fieldType,
                })}
              >
                <Typography variant='h4'>{splittedColumnName?.[0]}</Typography>
              </Link>
            </Box>
          );
        }
      }
    },
    [handleGetRedirectUrl],
  );

  useEffect(() => {
    if (paginationData) {
      setPagination(() => ({
        page: paginationData.offset / 100,
        count: Math.ceil(paginationData.total / paginationData.limit),
      }));
    }
  }, [paginationData]);

  useEffect(() => {
    if (rowsData) {
      const data = rowsData.reduce((acc, timesheet) => {
        if (timesheet.rowType === 'normal') {
          const newCols = timesheet.columns.reduce((columnAcc, _, i) => {
            switch (timesheet.columns[i].fieldId) {
              case 'timesheet.project':
              case 'timesheet.issue':
                return {
                  ...columnAcc,
                  [timesheet.columns[i].fieldId]:
                    `${timesheet.columns[i].value}$$${timesheet.columns[i].valueId}$$${timesheet.columns[i].fieldType}`,
                };
              default:
                return {
                  ...columnAcc,
                  [timesheet.columns[i].fieldId]: timesheet.columns[i].value,
                };
            }
          }, {});
          return [
            ...acc,
            { ...newCols, id: timesheet.rowId, services: timesheet.services },
          ];
        }
        if (timesheet.rowType === 'group') {
          const columnsTimesheetData = timesheet.columns
            .filter(column => column.value !== undefined)
            .map(column => `${column.fieldName}: ${column.value}`)
            .join(' ');
          const emptyRow = columnsData.reduce((obj, col) => {
            obj[col.fieldId] = null;
            return obj;
          }, {});
          return [
            ...acc,
            {
              id: timesheet.rowId,
              groupName: timesheet.rowName,
              groupData: columnsTimesheetData,
              ...emptyRow,
              isGroup: true,
            },
          ];
        }
        return acc;
      }, []);
      setGridRows(data);
    }
  }, [rowsData, columnsData]);

  useEffect(() => {
    if (columnsData) {
      const smallFields = ['date', 'hours', 'float', 'bool'];
      const data = columnsData.map(column => {
        return {
          field: column.fieldId,
          headerName: column.fieldName,
          renderHeader: props => (
            <Typography
              variant='h4'
              sx={{
                fontWeight: 700,
              }}
            >
              {props.colDef.headerName}
            </Typography>
          ),
          disableExport: false,
          position: column.position,
          sortable: column.canSort,
          flex: smallFields.includes(column.fieldType) ? 0 : 1,
          width: smallFields.includes(column.fieldType) && 120,
          renderCell: props => cellRenderer({ props, column }),
          cellClassName: props => {
            let cellClasses = [];
            if (!props.row.groupName) {
              cellClasses.push('tableCell');
            }
            if (props.row.groupName) {
              cellClasses.push('tableCell tableGroupCell');
            }
            return cellClasses;
          },
        };
      });
      setGridColumns(data);
    }
  }, [cellRenderer, columnsData]);

  const contextPopperStylePosition = useRef({
    left: 0,
    top: 0,
    maxWidth: 180,
  });

  const handleEditTimesheet = useCallback(() => {
    //let url = '';
    if (contextMenuData.currentRow) {
      editEvent(contextMenuData.currentRow.rowId);
      //url = `/timesheets/${contextMenuData.currentRow.rowId}/edit`;
    }

    if (contextMenuData.currentRows?.length > 1) {
      editEvent(contextMenuData.currentRows);
      //const timesheetsIds = contextMenuData.currentRows.join('||');
      //url = `/timesheets/bulk_edit?timesheets=${timesheetsIds}`;
    }
    //window.open(url, '_blank');
  }, [contextMenuData.currentRow, contextMenuData.currentRows, editEvent]);

  const handleChooseSubItemType = useCallback(
    (type, value, itemPosition) => {
      const itemsCount = 4;
      setContextMenuData(prevState => {
        return {
          ...prevState,
          isSecondStepVisible: !prevState.isSecondStepVisible,
          secondPopoverStepValue: value,
          secondStepType: type,
          positionInfo: {
            itemPosition: itemPosition,
            positionDelta: itemsCount - itemPosition - 2,
          },
        };
      });
    },
    [setContextMenuData],
  );

  const approveTimeshhet = useCallback(
    async values => {
      try {
        setLoading(true);
        const response = await queryForApproveTimesheet({ values });
        if (response) {
          setLoading(false);
          approveOrRejectTimesheets();
        }
      } catch (error) {
        setLoading(false);
        setTableErrorMessage(
          getLocalString('SpentTimeTable', 'errors', 'approveError'),
        );
        setTimeout(() => setTableErrorMessage(null), 2500);
      }
    },
    [approveOrRejectTimesheets, getLocalString],
  );
  const approveOvertime = useCallback(
    async values => {
      try {
        setLoading(true);
        const response = await updateTimesheets(values);
        if (response) {
          setLoading(false);
          approveOrRejectOvertime();
        }
      } catch (error) {
        setLoading(false);
        setTableErrorMessage(
          getLocalString('SpentTimeTable', 'errors', 'overtimeError'),
        );
        setTimeout(() => setTableErrorMessage(null), 2500);
      }
    },
    [approveOrRejectOvertime, getLocalString],
  );
  const handleDeleteTimesheet = useCallback(
    async ({ projectId, timesheetId, tableSelectedValues }) => {
      try {
        setLoading(true);
        const response = await deleteTimesheets(
          tableSelectedValues.map(item => {
            return { redmineId: item };
          }),
        );
        if (response) {
          setLoading(false);
          setOpenWarningModal(false);
          deleteTimesheet();
        }
      } catch (error) {
        setLoading(false);
        setTableErrorMessage(getLocalString('errors', 'deleteError'));
        setTimeout(() => setTableErrorMessage(null), 2500);
      }
    },
    [deleteTimesheet, getLocalString],
  );
  const handleChooseSubItem = useCallback(
    async value => {
      setContextMenuData(prevState => ({
        ...prevState,
        isSecondStepVisible: false,
      }));
      setIsContextMenuOpen(false);
      if (contextMenuData.secondStepType === 'approve') {
        await approveTimeshhet(
          tableSelectedValues?.map(selectedValueId => {
            return {
              redmineId: Number(selectedValueId),
              isApproved: value === 'approve',
            };
          }),
        );
      } else if (contextMenuData.secondStepType === 'overtime') {
        try {
          const valuesForUpdate = gridRows.filter(row => {
            return tableSelectedValues?.find(value => {
              return row.id === value;
            });
          });
          const formattedDataForUpdate = valuesForUpdate.map(selectedValue => {
            return {
              redmineId: selectedValue.id,
              projectId: selectedValue.services.projectId,
              issueId: Number(selectedValue.services.issueId),
              hours: selectedValue.services.hours,
              comments: selectedValue.services.comments,
              spentOn: selectedValue.services.spentOn,
              userId: selectedValue.services.userId,
              isOvertime: value === 'approve',
            };
          });
          await approveOvertime(formattedDataForUpdate);
        } catch (error) {
          console.error('ERROR ON CHOOSE', error);
        }
      }
    },
    [
      approveOvertime,
      approveTimeshhet,
      contextMenuData.secondStepType,
      gridRows,
      setContextMenuData,
      tableSelectedValues,
    ],
  );

  const handleUnselect = useCallback(() => {
    setIsContextMenuOpen(false);
    setContextMenuData(prevState => ({
      ...prevState,
      isSecondStepVisible: false,
      currentRow: {},
    }));
    setTableSelectedValues([]);
  }, [setContextMenuData]);

  const isDisabledMenuItems = {
    isProjectClosed: true,
    isVisibleApprove:
      !contextMenuData.currentRow.services?.canApprove ||
      contextMenuData.currentRow.services?.isProjectClosed,
    isVisibleCanEditOrOvertimeItem:
      contextMenuData.currentRow.services?.isApproved ||
      !contextMenuData.currentRow.services?.canEdit ||
      contextMenuData.currentRow.services?.isProjectClosed,
  };

  const isSelectedRowsIncludesClosedProjects = rowsData.filter(
    (row, index, array) => {
      const selectedValue = tableSelectedValues?.find(value => {
        return row.rowId === value;
      });
      if (selectedValue && row.services.isProjectClosed) {
        return array[index];
      }
    },
  );

  const isSelectedRowsIncludesNotApproved = rowsData.filter(
    (row, index, array) => {
      const selectedValue = tableSelectedValues?.find(value => {
        return row.rowId === value;
      });
      if (selectedValue && !row.services.canApprove) {
        return array[index];
      }
    },
  );
  const isSelectedRowsIncludesApproved = rowsData.filter(
    (row, index, array) => {
      const selectedValue = tableSelectedValues?.find(value => {
        return row.rowId === value;
      });
      if (selectedValue && row.services.isApproved) {
        return array[index];
      }
    },
  );

  const selectRow = useCallback(
    ({ rowId, clear = false }) => {
      setTableSelectedValues(prevState => {
        if (clear) {
          return [rowId];
        }
        if (prevState.includes(rowId)) {
          return prevState.filter(id => id !== rowId);
        }
        return [...prevState, rowId];
      });
      forceUpdate();
    },

    [setTableSelectedValues],
  );

  const handleRowContextMenu = useCallback(
    e => {
      e.preventDefault();
      if (!e.currentTarget) {
        return;
      }
      const groupRow = e.currentTarget.className.includes('groupRow');
      const rowId = e.currentTarget.getAttribute('data-id');
      if (!isContextMenuOpen && !groupRow) {
        const currentRow = rowsData.find(row => row.rowId === rowId);
        setContextMenuData(prevState => {
          prevState.currentRow = currentRow;
          prevState.currentRows = tableSelectedValues;
          prevState.isSecondStepVisible = false;
          prevState.secondStepType = '';
          return prevState;
        });
        contextPopperStylePosition.current.left = e.clientX;
        contextPopperStylePosition.current.top = e.clientY;
        if (!tableSelectedValues.includes(rowId)) {
          selectRow({ rowId: rowId });
        }
        setIsContextMenuOpen(true);
        //forceUpdate();
      }
    },
    [
      isContextMenuOpen,
      rowsData,
      selectRow,
      setContextMenuData,
      tableSelectedValues,
    ],
  );

  function Pagination({ page, onPageChange, className }) {
    const apiRef = useGridApiContext();
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);
    return (
      <MuiPagination
        color='primary'
        className={className}
        count={pagination.count}
        siblingCount={1}
        boundaryCount={0}
        page={pagination.page + 1}
        renderItem={item => {
          if (item.type === 'next') {
            return (
              !item.disabled && (
                <Box>
                  {getLocalString('labels', 'next')}
                  <PaginationItem
                    {...item}
                    slots={{
                      next: PaginationNextButtonIcon,
                    }}
                  ></PaginationItem>
                </Box>
              )
            );
          } else if (item.type === 'previous') {
            return (
              !item.disabled && (
                <Box>
                  <PaginationItem
                    {...item}
                    slots={{
                      previous: PaginationPreviousButtonIcon,
                    }}
                  ></PaginationItem>
                  {getLocalString('labels', 'previous')}
                </Box>
              )
            );
          } else {
            return <PaginationItem {...item}></PaginationItem>;
          }
        }}
        onChange={(event, newPage) => {
          changePagination(newPage - 1);
        }}
      />
    );
  }
  function CustomPagination(props) {
    return (
      <GridPagination
        {...props}
        labelDisplayedRows={() => ''}
        rowsPerPageOptions={[]}
        ActionsComponent={Pagination}
      />
    );
  }
  const handleSorting = useCallback(
    newSortModel => {
      const validSortModel = newSortModel.filter(
        item =>
          item &&
          typeof item.field === 'string' &&
          ['asc', 'desc'].includes(item.sort),
      );

      setSortModel(validSortModel);
      changeSorting(validSortModel);
    },
    [changeSorting],
  );

  const handleModalAction = useCallback(
    async e => {
      switch (e.currentTarget.attributes.value.value) {
        case 'save':
          await handleDeleteTimesheet({
            projectId: contextMenuData.currentRow.services.projectId,
            timesheetId: contextMenuData.currentRow.rowId,
            tableSelectedValues: tableSelectedValues,
          });
          break;
        case 'delete':
          setOpenWarningModal(false);
          break;
      }
    },
    [contextMenuData, handleDeleteTimesheet, tableSelectedValues],
  );

  const handleWarningModalOpen = useCallback(() => {
    setOpenWarningModal(true);
  }, []);
  const getPageSize = useCallback(() => {
    if (!rowsData || rowsData.length === 0) {
      return 300;
    }
    const maxReasonableSize = 1000;
    return Math.min(rowsData.length, maxReasonableSize);
  }, [rowsData]);
  return (
    <>
      {isSmallScreen ? (
        <></>
      ) : (
        <>
          <WarningModal
            issue
            simple
            isWarningModalOpen={openWarningModal}
            handleToggle={handleModalAction}
            closeWarningModal={() => setOpenWarningModal(false)}
            title={getLocalString('messages', 'confirmDelete')}
          />
          {loading && (
            <Grid
              container
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                zIndex: 99,
                position: 'absolute',
              }}
            >
              <CircularProgress color='secondary' />
            </Grid>
          )}
          {isContextMenuOpen && (
            <ClickAwayListener onClickAway={handleUnselect}>
              <StyledContextPopover
                keepMounted
                open={isContextMenuOpen}
                style={contextPopperStylePosition.current}
                anchorPosition={contextPopperStylePosition.current}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                anchorReference='none'
              >
                <TableSecondStepPopover
                  value={contextMenuData.secondPopoverStepValue}
                  handleChooseSubItem={handleChooseSubItem}
                  isSecondStepVisible={contextMenuData.isSecondStepVisible}
                  contextPopperStylePosition={contextPopperStylePosition}
                  positionInfo={contextMenuData.positionInfo}
                />
                <Grid container direction='column'>
                  <StyledContextMenuItem
                    disabledStyle={
                      !contextMenuData.currentRow.services.canEdit ||
                      isSelectedRowsIncludesApproved.length > 0 ||
                      isSelectedRowsIncludesClosedProjects.length > 0
                    }
                    item
                    xs={12}
                    onClick={handleEditTimesheet}
                    sx={{
                      padding: '6px 20px',
                    }}
                  >
                    <Typography variant='h5'>
                      {getLocalString('labels', 'edit')}
                    </Typography>
                  </StyledContextMenuItem>
                  <StyledContextMenuItem
                    disabledStyle={
                      isSelectedRowsIncludesNotApproved.length > 0 ||
                      isSelectedRowsIncludesClosedProjects.length > 0
                    }
                    container
                    wrap='nowrap'
                    onClick={() =>
                      handleChooseSubItemType(
                        'approve',
                        contextMenuData.currentRow.services.isApproved,
                        1,
                      )
                    }
                    sx={{
                      justifyContent: 'space-between',
                      padding: '6px 0 6px 20px',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant='h5'>
                      {getLocalString('labels', 'upproved')}
                    </Typography>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                      }}
                    >
                      <ChevronIcon
                        direction='right'
                        color='rgba(228, 228, 239, 1)'
                        viewBox='0 0 24 24'
                      />
                    </Box>
                  </StyledContextMenuItem>
                  <StyledContextMenuItem
                    disabledStyle={
                      isSelectedRowsIncludesApproved.length > 0 ||
                      isSelectedRowsIncludesClosedProjects.length > 0
                    }
                    container
                    wrap='nowrap'
                    onClick={() =>
                      handleChooseSubItemType(
                        'overtime',
                        contextMenuData.currentRow.services.isOvertime,
                        2,
                      )
                    }
                    sx={{
                      justifyContent: 'space-between',
                      padding: '6px 0 6px 20px',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant='h5'>
                      {getLocalString('labels', 'overtime')}
                    </Typography>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                      }}
                    >
                      <ChevronIcon
                        direction='right'
                        color='rgba(228, 228, 239, 1)'
                        viewBox='0 0 24 24'
                      />
                    </Box>
                  </StyledContextMenuItem>
                  <StyledContextMenuItem
                    disabledStyle={
                      isSelectedRowsIncludesApproved.length > 0 ||
                      isSelectedRowsIncludesClosedProjects.length > 0
                    }
                    item
                    xs={12}
                    onClick={() => {
                      setIsContextMenuOpen(false);
                      setContextMenuData(prevState => ({
                        ...prevState,
                        isSecondStepVisible: false,
                      }));
                      handleWarningModalOpen();
                    }}
                    sx={{
                      padding: '6px 20px',
                    }}
                  >
                    <Typography variant='h5'>
                      {getLocalString('labels', 'delete')}
                    </Typography>
                  </StyledContextMenuItem>
                </Grid>
              </StyledContextPopover>
            </ClickAwayListener>
          )}
          <Grid
            container
            sx={{
              flexDirection: 'column',
              flex: 1,
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <Grid
              container
              sx={{
                justifyContent: tableErrorMessage
                  ? 'space-between'
                  : 'flex-end',
              }}
            >
              {typeof tableErrorMessage === 'string' && (
                <Typography
                  variant='h5'
                  sx={{
                    fontWeight: 600,
                    color: '#E03737',
                  }}
                >
                  {tableErrorMessage}
                </Typography>
              )}
            </Grid>
            <StyledDataGridTableRoot
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 300, page: 0 },
                },
              }}
              localeText={{
                noRowsLabel: getLocalString('labels', 'noData'),
                footerRowSelected: count =>
                  count !== 1
                    ? `${count.toLocaleString()} ${getLocalString('labels', 'selectedRows')}`
                    : `${count.toLocaleString()} ${getLocalString('labels', 'selectedRow')}`,
              }}
              pagination
              {...{ ['checkboxSelection']: currentType !== 1 }}
              disableColumnFilter
              disableRowSelectionOnClick
              disableColumnMenu
              columns={gridColumns}
              rows={gridRows}
              rowSelectionModel={tableSelectedValues}
              onRowSelectionModelChange={newRowSelectionModel => {
                setTableSelectedValues(newRowSelectionModel);
              }}
              getRowId={item => item.id}
              sortModel={Array.isArray(sortModel) ? sortModel : []}
              onSortModelChange={handleSorting}
              sortingMode='server'
              slotProps={{
                row: {
                  onContextMenu: e => handleRowContextMenu(e),
                  style: { cursor: 'context-menu' },
                },
                baseCheckbox: {
                  icon: (
                    <CheckboxIcon width='12' height='12' viewBox='0 0 12 12' />
                  ),
                  checkedIcon: (
                    <CheckboxIconChecked
                      width='12'
                      height='12'
                      viewBox='0 0 12 12'
                    />
                  ),
                },
              }}
              slots={{
                pagination: CustomPagination,
                columnUnsortedIcon: () => (
                  <SortIcon width='20' height='20' viewBox='0 0 20 20' />
                ),
                columnSortedAscendingIcon: () => (
                  <SortIcon
                    direction='up'
                    width='20'
                    height='20'
                    viewBox='0 0 20 20'
                  />
                ),
                columnSortedDescendingIcon: () => (
                  <SortIcon
                    direction='down'
                    width='20'
                    height='20'
                    viewBox='0 0 20 20'
                  />
                ),
              }}
              getRowClassName={params => params.row.groupName && 'groupRow'}
              isRowSelectable={params => !params.row.groupName}
              getRowHeight={() => 'auto'}
              sx={{
                '& .MuiTablePagination-spacer': {
                  display: 'none',
                },
              }}
            />
            {/*              <Box display="flex" wrap="nowrap">
                {originalTimesheetsData[0].columns.map(column => {
                  return (
                    <Box key={column.fieldId} display="flex" flexWrap="nowrap" alignItems="center" marginLeft={2}>
                      <Typography variant="h5" marginRight={2}>
                        {getTranslatedText(intl, 'filter', column.fieldId, column.fieldName)}:
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {column.value ? column.value : 0}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>*/}
          </Grid>
        </>
      )}
    </>
  );
};

export default memo(SpentTimeTable);
